// Centralized mock data generators for the showcase
// Keeps page files focused on UI instead of hardcoded data

const firstNames = ['Sarah', 'James', 'Maria', 'Robert', 'Emily', 'Michael', 'Jessica', 'David', 'Ashley', 'Daniel', 'Lauren', 'Chris', 'Megan', 'Kevin', 'Rachel'];
const lastNames = ['Johnson', 'Chen', 'Garcia', 'Williams', 'Patel', 'Kim', 'Anderson', 'Martinez', 'Thompson', 'Lee', 'Wilson', 'Taylor', 'Brown', 'Davis', 'Moore'];
const companies = ['Acme Corp', 'Global Tech', 'Pinnacle Solutions', 'Summit Industries', 'Vertex Systems', 'Horizon Group', 'Atlas Partners', 'Nova Dynamics', 'Apex Digital', 'Orbit Technologies', 'Catalyst Inc', 'Zenith Corp', 'Forge Innovations', 'Pulse Networks', 'Beacon Analytics'];
const stages = ['Prospecting', 'Qualification', 'Needs Analysis', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
const kanbanStages = ['Discovery', 'Qualification', 'Proposal', 'Negotiation', 'Closing'];
const products = ['Enterprise Suite', 'Pro License', 'Analytics Add-on', 'Support Package', 'Custom Integration', 'Data Migration', 'Training Bundle', 'API Access'];
const statuses = ['Active', 'Inactive', 'Pending', 'At Risk', 'Churned'];
const priorities = ['High', 'Medium', 'Low', 'Critical'];
const eventTypes = ['call', 'email', 'meeting', 'task', 'stage_change'];

const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomBetween = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomCurrency = (min, max) => Math.round((Math.random() * (max - min) + min) * 100) / 100;

const generateName = () => `${randomItem(firstNames)} ${randomItem(lastNames)}`;

export const generateAccounts = (count = 50) => {
  return Array.from({ length: count }, (_, i) => ({
    id: `acc-${i + 1}`,
    name: companies[i % companies.length] + (i >= companies.length ? ` ${Math.floor(i / companies.length) + 1}` : ''),
    industry: randomItem(['Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education', 'Energy']),
    revenue: randomCurrency(50000, 5000000),
    employees: randomBetween(10, 5000),
    status: randomItem(statuses),
    owner: generateName(),
    phone: `(${randomBetween(200, 999)}) ${randomBetween(200, 999)}-${randomBetween(1000, 9999)}`,
    website: `www.${companies[i % companies.length].toLowerCase().replace(/\s+/g, '')}.com`,
    lastActivity: `2025-${String(randomBetween(1, 12)).padStart(2, '0')}-${String(randomBetween(1, 28)).padStart(2, '0')}`,
    rating: randomItem(['Hot', 'Warm', 'Cold']),
    growth: randomBetween(-15, 40),
    health: randomBetween(30, 100),
  }));
};

export const generateOpportunities = (count = 50) => {
  return Array.from({ length: count }, (_, i) => {
    const stage = randomItem(stages);
    const isClosed = stage.startsWith('Closed');
    return {
      id: `opp-${i + 1}`,
      name: `${randomItem(companies)} - ${randomItem(products)}`,
      account: randomItem(companies),
      amount: randomCurrency(5000, 500000),
      stage,
      probability: isClosed ? (stage === 'Closed Won' ? 100 : 0) : randomBetween(10, 90),
      closeDate: `2025-${String(randomBetween(1, 12)).padStart(2, '0')}-${String(randomBetween(1, 28)).padStart(2, '0')}`,
      owner: generateName(),
      product: randomItem(products),
      type: randomItem(['New Business', 'Renewal', 'Upsell', 'Cross-sell']),
      nextStep: randomItem(['Follow up call', 'Send proposal', 'Demo scheduled', 'Contract review', 'Awaiting approval']),
      lastActivity: `${randomBetween(0, 14)} days ago`,
      created: `2025-${String(randomBetween(1, 6)).padStart(2, '0')}-${String(randomBetween(1, 28)).padStart(2, '0')}`,
    };
  });
};

export const generateKanbanData = () => {
  return kanbanStages.map(stage => ({
    id: stage.toLowerCase().replace(/\s+/g, '-'),
    name: stage,
    cards: Array.from({ length: randomBetween(3, 7) }, (_, i) => ({
      id: `${stage.toLowerCase()}-${i + 1}`,
      name: `${randomItem(companies)} - ${randomItem(products)}`,
      account: randomItem(companies),
      amount: randomCurrency(10000, 300000),
      owner: generateName(),
      closeDate: `2025-${String(randomBetween(6, 12)).padStart(2, '0')}-${String(randomBetween(1, 28)).padStart(2, '0')}`,
      probability: randomBetween(10, 90),
      priority: randomItem(priorities),
      daysInStage: randomBetween(1, 30),
    })),
  }));
};

export const generateTimeSeries = (months = 12, series = 1) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const labels = monthNames.slice(0, months);

  const datasets = Array.from({ length: series }, (_, s) => {
    let value = randomBetween(40, 80);
    return labels.map(() => {
      value = Math.max(10, Math.min(100, value + randomBetween(-15, 20)));
      return value;
    });
  });

  return { labels, datasets };
};

export const generateRevenueData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let actual = 180000;
  let target = 200000;
  return {
    labels: months,
    actual: months.map(() => {
      actual = actual + randomBetween(-20000, 40000);
      return Math.max(100000, actual);
    }),
    target: months.map(() => {
      target = target + randomBetween(5000, 15000);
      return target;
    }),
  };
};

export const generatePipelineByStage = () => {
  return {
    labels: ['Prospecting', 'Qualification', 'Proposal', 'Negotiation', 'Closing'],
    values: [randomBetween(800, 1200) * 1000, randomBetween(600, 1000) * 1000, randomBetween(400, 800) * 1000, randomBetween(300, 600) * 1000, randomBetween(200, 400) * 1000],
  };
};

export const generateTeamPerformance = () => {
  const members = Array.from({ length: 5 }, () => generateName());
  return {
    labels: ['Revenue', 'Pipeline', 'Win Rate', 'Activity', 'Retention'],
    datasets: members.map(name => ({
      name,
      values: Array.from({ length: 5 }, () => randomBetween(40, 95)),
    })),
  };
};

export const generateCalendarEvents = () => {
  const eventNames = ['Team Standup', 'Client Review', 'Sprint Planning', 'QBR Prep', 'Pipeline Review', 'Training Session', 'Board Meeting', 'Product Demo', '1:1 Meeting', 'Strategy Session'];
  const colors = ['#0176d3', '#2e844a', '#fe9339', '#c23934', '#9050e9', '#06a59a'];
  const events = [];

  for (let day = 1; day <= 28; day++) {
    const numEvents = randomBetween(0, 3);
    for (let e = 0; e < numEvents; e++) {
      const startHour = randomBetween(8, 16);
      events.push({
        id: `evt-${day}-${e}`,
        title: randomItem(eventNames),
        date: `2025-07-${String(day).padStart(2, '0')}`,
        startHour,
        endHour: startHour + randomBetween(1, 2),
        color: randomItem(colors),
        attendees: Array.from({ length: randomBetween(1, 4) }, () => generateName()),
        location: randomItem(['Zoom', 'Conference Room A', 'Conference Room B', 'Google Meet', 'Teams']),
      });
    }
  }
  return events;
};

export const generateActivityFeed = (count = 25) => {
  return Array.from({ length: count }, (_, i) => {
    const type = randomItem(eventTypes);
    const base = {
      id: `act-${i + 1}`,
      type,
      user: generateName(),
      timestamp: new Date(2025, 6, randomBetween(1, 28), randomBetween(8, 18), randomBetween(0, 59)).toISOString(),
      account: randomItem(companies),
    };

    switch (type) {
      case 'call':
        return { ...base, duration: `${randomBetween(5, 45)} min`, outcome: randomItem(['Connected', 'Voicemail', 'No Answer']), notes: 'Discussed upcoming renewal and expansion opportunities.' };
      case 'email':
        return { ...base, subject: randomItem(['Re: Proposal Update', 'Follow-up from our meeting', 'Q3 Planning', 'Contract Review', 'Next Steps']), opened: Math.random() > 0.3 };
      case 'meeting':
        return { ...base, title: randomItem(['Quarterly Review', 'Product Demo', 'Kickoff Meeting', 'Strategy Session']), attendees: Array.from({ length: randomBetween(2, 5) }, () => generateName()), duration: `${randomBetween(30, 90)} min` };
      case 'task':
        return { ...base, title: randomItem(['Send proposal', 'Update CRM', 'Schedule follow-up', 'Review contract', 'Prepare deck']), dueDate: `2025-07-${String(randomBetween(1, 28)).padStart(2, '0')}`, priority: randomItem(priorities), completed: Math.random() > 0.4 };
      case 'stage_change':
        return { ...base, opportunity: `${randomItem(companies)} - ${randomItem(products)}`, fromStage: randomItem(stages.slice(0, 3)), toStage: randomItem(stages.slice(2, 5)), amount: randomCurrency(20000, 200000) };
      default:
        return base;
    }
  }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

export const generateWizardData = () => ({
  accounts: Array.from({ length: 10 }, (_, i) => ({
    id: `wiz-acc-${i}`,
    name: companies[i],
    industry: randomItem(['Technology', 'Healthcare', 'Finance']),
    type: randomItem(['Customer', 'Prospect', 'Partner']),
  })),
  products: products.map((p, i) => ({
    id: `prod-${i}`,
    name: p,
    price: randomCurrency(500, 25000),
    category: randomItem(['Software', 'Services', 'Hardware']),
    description: `Enterprise-grade ${p.toLowerCase()} for growing businesses.`,
  })),
});

export const generateHeatmapData = () => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 12 }, (_, i) => `${i + 8}:00`);
  return {
    days,
    hours,
    values: days.map(() => hours.map(() => randomBetween(0, 100))),
  };
};

export const generateScatterData = (count = 40) => {
  return Array.from({ length: count }, () => ({
    x: randomBetween(10, 120),
    y: randomCurrency(5000, 300000),
    won: Math.random() > 0.35,
    label: randomItem(companies),
  }));
};

// ---- Timeline Activity Feed data ---- //

const caseSubjects = [
  'Login page returns 500 error', 'Cannot export CSV reports', 'SSO integration failing',
  'API rate limit exceeded', 'Data sync lag over 30 minutes', 'Permission set not applying',
  'Dashboard widgets not loading', 'Email templates rendering incorrectly',
  'Mobile app crashes on launch', 'Bulk import timing out',
];

const meetingAgendas = [
  'Review Q3 roadmap priorities and resource allocation',
  'Discuss migration timeline and rollback plan',
  'Walk through new feature requirements and acceptance criteria',
  'Quarterly business review and success metrics',
  'Technical architecture review for upcoming release',
  'Customer feedback synthesis and action items',
];

const visitLocations = [
  'San Francisco HQ - Building A', 'Chicago Office - 14th Floor', 'Austin Campus - East Wing',
  'New York Downtown Office', 'Seattle Innovation Center', 'Denver Regional Hub',
];

const fileNames = [
  'Q3_Proposal_v2.pdf', 'Contract_Amendment.docx', 'Technical_Specs.xlsx',
  'Architecture_Diagram.png', 'Meeting_Recording.mp4', 'Budget_Forecast.xlsx',
  'SOW_Final.pdf', 'Security_Audit_Report.pdf', 'Onboarding_Guide.pptx', 'API_Documentation.md',
];

const approvalTitles = [
  'Discount approval: 25% off Enterprise tier', 'Non-standard payment terms: Net 90',
  'Custom SLA amendment request', 'Budget override for Q3 expansion',
  'Security exception for legacy integration', 'Executive sponsorship for pilot program',
];

const subscriptionPlans = ['Starter', 'Professional', 'Enterprise', 'Unlimited'];
const caseOrigins = ['Web', 'Phone', 'Email', 'Chat', 'Social Media'];
const orderStatuses = ['Draft', 'Submitted', 'Fulfilled', 'Shipped', 'Delivered', 'Cancelled'];
const escalationReasons = ['SLA breach', 'Customer executive complaint', 'Repeated issue', 'Revenue impact', 'Churn risk', 'Security concern'];

const timelineTypes = [
  { type: 'email', weight: 18 },
  { type: 'call', weight: 16 },
  { type: 'task', weight: 14 },
  { type: 'meeting', weight: 10 },
  { type: 'stage_change', weight: 7 },
  { type: 'case', weight: 8 },
  { type: 'order', weight: 5 },
  { type: 'note', weight: 5 },
  { type: 'attachment', weight: 4 },
  { type: 'site_visit', weight: 3 },
  { type: 'escalation', weight: 3 },
  { type: 'subscription_change', weight: 4 },
  { type: 'approval', weight: 3 },
];

const pickWeightedType = () => {
  const totalWeight = timelineTypes.reduce((s, t) => s + t.weight, 0);
  let r = Math.random() * totalWeight;
  for (const t of timelineTypes) {
    r -= t.weight;
    if (r <= 0) return t.type;
  }
  return 'email';
};

export const generateTimelineData = (count = 60) => {
  const now = new Date(2025, 6, 28, 17, 0);
  const parentTypes = ['Account', 'Contact', 'Opportunity'];

  return Array.from({ length: count }, (_, i) => {
    const type = pickWeightedType();
    const daysAgo = randomBetween(0, 90);
    const ts = new Date(now.getTime() - daysAgo * 86400000 - randomBetween(0, 86400000));
    const user = generateName();
    const account = randomItem(companies);
    const contact = Math.random() > 0.3 ? generateName() : null;
    const parentType = randomItem(parentTypes);

    const base = {
      id: `tl-${i + 1}`,
      type,
      user,
      timestamp: ts.toISOString(),
      account,
      contact,
      parentRecord: parentType,
      parentRecordName: parentType === 'Account' ? account : parentType === 'Contact' ? (contact || generateName()) : `${account} - ${randomItem(products)}`,
      isPinned: Math.random() < 0.05,
      isMilestone: Math.random() < 0.04,
    };

    switch (type) {
      case 'call':
        return { ...base, duration: randomBetween(3, 55), outcome: randomItem(['Connected', 'Voicemail', 'No Answer', 'Left Message', 'Busy']), sentiment: randomItem(['Positive', 'Neutral', 'Negative']), notes: randomItem(['Discussed renewal timeline and pricing options.', 'Left voicemail regarding contract update.', 'Confirmed next steps and scheduled follow-up.', 'Addressed concerns about implementation timeline.']) };
      case 'email':
        return { ...base, subject: randomItem(['Re: Proposal Update', 'Follow-up from our meeting', 'Q3 Planning Discussion', 'Contract Review Required', 'Next Steps & Action Items', 'Introduction & Overview', 'Pricing Confirmation', 'Technical Requirements']), direction: randomItem(['sent', 'received']), opened: Math.random() > 0.25, bodyPreview: randomItem(['Thanks for the update. I have reviewed the proposal and have a few questions...', 'Following up on our conversation last week. Please find attached...', 'Could we schedule time to discuss the implementation plan?', 'I wanted to share the latest numbers from our analysis...']), attachmentCount: Math.random() > 0.6 ? randomBetween(1, 3) : 0 };
      case 'meeting':
        return { ...base, title: randomItem(['Quarterly Business Review', 'Product Demo', 'Kickoff Meeting', 'Strategy Session', 'Sprint Planning', 'Executive Briefing']), attendees: Array.from({ length: randomBetween(2, 6) }, () => generateName()), duration: randomBetween(25, 90), location: randomItem(['Zoom', 'Google Meet', 'Teams', 'Conference Room A', 'Conference Room B']), actionItems: Array.from({ length: randomBetween(1, 4) }, () => randomItem(['Send follow-up email', 'Update proposal', 'Schedule next review', 'Share technical docs', 'Prepare demo environment'])), agenda: randomItem(meetingAgendas) };
      case 'task':
        return { ...base, title: randomItem(['Send proposal', 'Update CRM records', 'Schedule follow-up', 'Review contract terms', 'Prepare presentation deck', 'Complete security questionnaire', 'Submit pricing approval']), dueDate: new Date(now.getTime() + randomBetween(-14, 21) * 86400000).toISOString().slice(0, 10), priority: randomItem(priorities), completed: Math.random() > 0.45, description: randomItem(['Review and finalize the proposal document before sending to client.', 'Ensure all contact information is up to date in the system.', 'Follow up on action items from the last meeting.', 'Prepare materials for the upcoming quarterly review.']) };
      case 'stage_change':
        { const fromIdx = randomBetween(0, 4); return { ...base, opportunity: `${account} - ${randomItem(products)}`, fromStage: stages[fromIdx], toStage: stages[Math.min(fromIdx + 1, 6)], amount: randomCurrency(15000, 350000), probability: randomBetween(10, 95) }; }
      case 'case':
        return { ...base, caseNumber: `CS-${randomBetween(10000, 99999)}`, subject: randomItem(caseSubjects), status: randomItem(['New', 'Working', 'Escalated', 'Waiting on Customer', 'Closed']), priority: randomItem(['Critical', 'High', 'Medium', 'Low']), origin: randomItem(caseOrigins), slaProgress: randomBetween(10, 100) };
      case 'order':
        return { ...base, orderNumber: `ORD-${randomBetween(100000, 999999)}`, totalAmount: randomCurrency(2500, 250000), status: randomItem(orderStatuses), lineItemCount: randomBetween(1, 12), shipDate: new Date(now.getTime() + randomBetween(1, 30) * 86400000).toISOString().slice(0, 10) };
      case 'note':
        return { ...base, title: randomItem(['Meeting Follow-up Notes', 'Client Feedback Summary', 'Internal Discussion Points', 'Action Items Update', 'Weekly Status Notes']), body: randomItem(['Key takeaways from today\'s meeting: 1) Timeline confirmed for Q3 launch 2) Budget approved with minor adjustments 3) Need to schedule technical review by end of week.', 'Client expressed interest in expanding the current contract. They want to add 50 more seats and upgrade to the Enterprise tier. Follow up with pricing by Thursday.', 'Discussion with engineering team about custom integration requirements. They estimate 3 weeks for development, plus 1 week for testing.', 'Updated the stakeholder map based on recent org changes. New VP of Engineering is now a key decision maker.']), relatedTo: `${account} - ${randomItem(['Account', 'Opportunity', 'Contact'])}` };
      case 'attachment':
        { const fn = randomItem(fileNames); const ext = fn.split('.').pop(); return { ...base, fileName: fn, fileType: ext, fileSize: `${randomBetween(50, 15000)} KB`, relatedTo: `${account} - ${randomItem(['Proposal', 'Contract', 'SOW', 'Report'])}` }; }
      case 'site_visit':
        return { ...base, location: randomItem(visitLocations), contactMet: contact || generateName(), visitDate: ts.toISOString().slice(0, 10), notes: randomItem(['Toured the new facility. Discussed expansion needs and timeline.', 'Met with technical team to review infrastructure requirements.', 'On-site training session completed. Team is ramping up well.', 'Facility audit completed. All systems operational.']), photoCount: randomBetween(0, 12) };
      case 'escalation':
        return { ...base, caseReference: `CS-${randomBetween(10000, 99999)}`, severity: randomItem(['P1', 'P2', 'P3']), reason: randomItem(escalationReasons), escalatedTo: generateName() };
      case 'subscription_change':
        { const changeType = randomItem(['Upgrade', 'Downgrade', 'Cancel', 'Renewal']); const mrrImpact = changeType === 'Upgrade' || changeType === 'Renewal' ? randomBetween(500, 8000) : -randomBetween(500, 8000); return { ...base, changeType, product: randomItem(products), mrrImpact, oldPlan: randomItem(subscriptionPlans), newPlan: changeType === 'Cancel' ? 'None' : randomItem(subscriptionPlans), effectiveDate: new Date(now.getTime() + randomBetween(-7, 30) * 86400000).toISOString().slice(0, 10) }; }
      case 'approval':
        return { ...base, title: randomItem(approvalTitles), status: randomItem(['Pending', 'Approved', 'Rejected']), submittedBy: user, approvers: Array.from({ length: randomBetween(1, 3) }, () => ({ name: generateName(), status: randomItem(['Approved', 'Pending', 'Rejected']) })), comments: randomItem(['Please review urgently - customer is waiting.', 'Standard approval flow per policy.', 'Requires VP sign-off per discount policy.', '']) };
      default:
        return base;
    }
  }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

export const SLDS_COLORS = ['#0176d3', '#2e844a', '#fe9339', '#c23934', '#9050e9', '#06a59a'];
